import { useState } from 'react';
import { Message } from '@fx-ui/fine-design';
import { CONTACTS, ECode } from '~/constants';
import { get, getErrorMsg, includes, post, size } from '~/utils';

interface UseImportMemberOptions {
    /** 导入接口地址 */
    importApi: string;
}

export interface ImportUploadResult {
    file: {
        key: string;
        [key: string]: unknown;
    };
}

export interface ImportInfo<TCanceledMember = unknown> {
    canceled?: TCanceledMember[];
    invitation_err_code?: number | string;
    members?: Record<string, unknown>[];
    token?: string;
}

interface ImportResult<TImportInfo> {
    failCount: number;
    importInfo: TImportInfo;
    successCount: number;
}

/**
 * 成员导入请求和结果状态
 */
const useImportMember = <TImportInfo extends ImportInfo>(props: UseImportMemberOptions) => {
    const { importApi } = props;
    const [importInfo, setImportInfo] = useState<TImportInfo>();

    const importMember = (uploadResult: ImportUploadResult) => {
        return post<TImportInfo>(
            importApi,
            { ...uploadResult.file, fileKey: uploadResult.file.key },
            { customHandleHttpCodes: [400] }
        ).then(
            (res): ImportResult<TImportInfo> => {
                const nextImportInfo = res?.data ?? {};
                const successCount = size(nextImportInfo.members);
                const invitationErrCode = nextImportInfo.invitation_err_code;

                if (includes(CONTACTS.INVITE_EXCEED_CODE, invitationErrCode)) {
                    Message.warning(getErrorMsg({ code: invitationErrCode }));
                }

                setImportInfo(nextImportInfo);
                return {
                    failCount: size(nextImportInfo.canceled),
                    importInfo: nextImportInfo,
                    successCount
                };
            },
            (error) => {
                const errorData = get(error, 'response.data', {});
                if (errorData.code === ECode.TENANT_FEATURE_REQUIRES_AUTH_APPROVAL) {
                    Message.warning(getErrorMsg(errorData));
                }
                return Promise.reject(error);
            }
        );
    };

    return { importMember, importInfo };
};

export default useImportMember;
